import { BaseError } from '.'
import { AssertError, Extensions } from './error'

/**
 * Local error codes
 */
enum ErrorCode {
  InstanceOf = 'ASSERT_INSTANCE_OF',
  Thenable = 'THENABLE',
}

/**
 * Classic assert
 */
export const assert: (
  condition: any,
  errorCodeOrConstructor: string | typeof BaseError,
  extensions?: Extensions
) => asserts condition = (condition, errorCodeOrConstructor, extensions) => {
  if (!condition) {
    if (!errorCodeOrConstructor || typeof errorCodeOrConstructor === 'string') {
      // @ts-ignore remove ignore when https://github.com/pulumi/pulumi/pull/10122 is resolved
      throw new AssertError(errorCodeOrConstructor, extensions)
    }
    const Err = errorCodeOrConstructor
    throw new Err(undefined, extensions)
  }
}

/**
 * Assert a value is of type
 */
type AssertInstanceOf = <T extends Gaia.AnyConstructor>(
  instance: any,
  type: T
) => asserts instance is InstanceType<T>

/**
 * Assert that a given object is an instance of type
 */
export const assertInstanceOf: AssertInstanceOf = (instance, type) => {
  assert(instance instanceof (type as any), ErrorCode.InstanceOf)
}

/**
 * Assert a value is thenable (like a promise)
 */
type AssertThenable = (value: any) => asserts value is PromiseLike<any>

/**
 * Assert a value is thenable
 */
export const assertThenable: AssertThenable = (value) => {
  assert(!!value && typeof value['then'] === 'function', ErrorCode.Thenable)
}

/**
 * Assert a value exists (is not undefined)
 */
export const assertExists = <T>(val?: T, msg?: string) => {
  assert(typeof val !== 'undefined', msg ?? 'Value does not exist')
}

/**
 * Signature for non nullish assertion function
 */
type AssertNotNullish = <T>(
  val?: T,
  msg?: string
) => asserts val is Exclude<T, undefined | null>

/**
 * Assert a value exists (is not undefined) and is not null
 */
export const assertNotNullish: AssertNotNullish = (val: any, msg?: string) => {
  assert(typeof val !== 'undefined' && val !== null, msg ?? 'Value not nullish')
}

/**
 * Assert a line of code is unreachable. Useful to guarantee switch
 * statements are exhaustive.
 */
export const assertUnreachable = (_: never): never => {
  throw new AssertError('Unreachable')
}
